import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BaseComponentImpl } from '../../../types/implementations';
import { Building } from '../../../types/protocols';
import ComponentDetailsView from '../../ComponentPage/ComponentDetailsView';
import ReportIssueModal from '../../../Components/ReportIssueModal';
import ComponentActionsDropdown from '../../../Components/ComponentActionsDropdown';
import { DeleteComponentModal } from '../../Förvaltning/ComponentValidation/DeleteComponentModal';
import { useToast } from '../../../Components/ToastContext';
import { ComponentFieldPatch } from '../../../Helpers/componentPatch';
import '../../Room/RoomComponentDetailsPane.css';

interface ComponentDetailPaneProps {
    selectedComponent: BaseComponentImpl | null;
    building: Building;
    buildingKey: string;
    hasComponents: boolean;
    /** Shown in a modal, which scrolls it; skips the sticky sidebar sizing. */
    inModal?: boolean;
    /** Offers admins "Ta bort komponent"; called once the component is deleted */
    onDeleted?: () => void;
    /** For a host that keeps its own copy of the building: an edit saved in the pane */
    onComponentPatched?: (componentKey: string, patch: ComponentFieldPatch) => void;
}

const ComponentDetailPane: React.FC<ComponentDetailPaneProps> = ({
    selectedComponent,
    building,
    buildingKey,
    hasComponents,
    inModal = false,
    onDeleted,
    onComponentPatched,
}) => {
    const [showReportIssueModal, setShowReportIssueModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { showToast } = useToast();

    if (selectedComponent) {
        return (
            <div style={inModal ? undefined : { position: 'sticky', top: '20px' }}>
                <ComponentDetailsView
                    component={selectedComponent}
                    building={building}
                    buildingKey={buildingKey}
                    showHeader={true}
                    sectionGap={20}
                    headingGap={10}
                    onComponentPatched={onComponentPatched}
                    headerActions={
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Link
                                className="open-tab-button"
                                to={`/building/${buildingKey}/component/${selectedComponent.key}`}
                                target="_blank"
                                title="Öppna i ny flik"
                            >
                                ↗
                            </Link>
                            <ComponentActionsDropdown
                                buildingKey={buildingKey}
                                componentKey={selectedComponent.key}
                                onReportIssue={() => setShowReportIssueModal(true)}
                                showOpenInNewTab={false}
                                resetTrigger={selectedComponent.key}
                                onDelete={onDeleted ? () => setShowDeleteModal(true) : undefined}
                            />
                        </div>
                    }
                    // In a modal the content sits flat on the modal body, which pads it
                    containerClassName={inModal ? '' : undefined}
                    containerStyle={inModal ? { padding: 0 } : {
                        maxHeight: 'calc(100vh - 40px)',
                        overflowY: 'auto',
                        overscrollBehavior: 'contain',
                    }}
                />

                <ReportIssueModal
                    show={showReportIssueModal}
                    onHide={() => setShowReportIssueModal(false)}
                    buildingKey={buildingKey}
                    componentKey={selectedComponent.key}
                    onSuccess={() => showToast('Felanmälan skickad')}
                />

                {showDeleteModal && onDeleted && (
                    <DeleteComponentModal
                        component={selectedComponent}
                        building={building}
                        buildingKey={buildingKey}
                        onDeleted={() => {
                            setShowDeleteModal(false);
                            showToast('Komponenten togs bort');
                            onDeleted();
                        }}
                        onClose={() => setShowDeleteModal(false)}
                    />
                )}
            </div>
        );
    }

    if (!hasComponents) return null;

    return (
        <div
            style={{
                width: '100%',
                minHeight: '400px',
                border: '2px dashed #ccc',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fafafa',
                position: 'sticky',
                top: '20px',
                maxHeight: 'calc(100vh - 40px)',
                overflowY: 'auto',
                overscrollBehavior: 'contain'
            }}
        >
            <p
                style={{
                    color: '#666',
                    fontSize: '16px',
                    textAlign: 'center',
                    margin: 0,
                    padding: '20px'
                }}
            >
                Klicka på en komponent för att visa mer info här
            </p>
        </div>
    );
};

export default ComponentDetailPane;
